'use client';

import {
  UhxBetaIcon,
  UhxInfoDamage,
  UhxInfoReplacement,
  UhxInfoShield,
  UhxInfoSupport,
  // UhxInfoTradeIn,
  UhxInfoWeightLoss,
  UhxInfoPregnancy,
} from 'assets/icons';
import { useTranslations } from 'next-intl';
import { UhxShopifyMap } from 'content/products/uhx';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { CustomImage } from '../custom-image';
import {
  ImageContainer,
  InfoModalContainer,
  InfoModalDescription,
  InfoModalTitle,
  PricingModal,
} from './layout';
import styles from './uhx.module.css';

export const UhxCartInfoModal = () => {
  const t = useTranslations('ring');
  const CartClient = useCartStore(
    useShallow((s) => ({ store: s.store, currency: s.currency })),
  );
  const formatPriceWithComma = useFormatPrice();

  const { store, currency } = CartClient;
  const CurrencyCode = CurrencySymbol[currency ?? Currency.usd];

  const benefits = [
    {
      icon: <UhxInfoDamage />,
      title: t('ring.infoModals.uhx.text.b.title'),
      description: t('ring.infoModals.uhx.text.b.p1'),
    },
    {
      icon: <UhxInfoReplacement />,
      title: t('ring.infoModals.uhx.text.c.title'),
      description: t('ring.infoModals.uhx.text.c.p1'),
    },
    {
      icon: <UhxInfoShield />,
      title: t('ring.infoModals.uhx.text.d.title'),
      description: t('ring.infoModals.uhx.text.d.p1'),
      disclaimer: t('ring.infoModals.uhx.text.d.disclaimer'),
    },
    {
      icon: <UhxInfoWeightLoss />,
      title: t('ring.infoModals.uhx.text.e.title'),
      description: t('ring.infoModals.uhx.text.e.p1'),
      disclaimer: t('ring.infoModals.uhx.text.e.disclaimer'),
    },
    {
      icon: <UhxInfoPregnancy />,
      title: t('ring.infoModals.uhx.text.pregnancy.title'),
      description: t('ring.infoModals.uhx.text.pregnancy.p1'),
      disclaimer: t('ring.infoModals.uhx.text.pregnancy.disclaimer'),
    },
    {
      icon: <UhxInfoSupport />,
      title: t('ring.infoModals.uhx.text.f.title'),
      description: t('ring.infoModals.uhx.text.f.p1'),
    },
  ];

  return (
    <InfoModalContainer>
      <InfoModalTitle>
        <h3>{t('ring.infoModals.uhx.title')}</h3>
        <p>
          <>
            {CurrencyCode}
            {formatPriceWithComma(
              UhxShopifyMap[store as ShopifyStore].Year1.price,
            )}
          </>
          {t('ring.infoModals.uhx.subtitle1')}
        </p>
        <ImageContainer className="uhx-image-container">
          <CustomImage
            src="web_v2/cart-info-modals/uhx-2.png"
            width={1376}
            height={808}
            alt={t('ring.UltrahumanX')}
          />
        </ImageContainer>
      </InfoModalTitle>
      <InfoModalDescription>
        <p>{t('ring.infoModals.uhx.description')}</p>
      </InfoModalDescription>
      <div className={styles.uhxInfoDescription}>
        {benefits.map((benefit, index) => (
          <div key={index} className={styles.benefit}>
            {benefit.icon}
            <h3>{benefit.title}</h3>
            <p className={styles.description}>{benefit.description}</p>
            <p className={styles.disclaimer}>{benefit.disclaimer}</p>
          </div>
        ))}
      </div>
    </InfoModalContainer>
  );
};

export const HomeUhxCartInfoModal = () => {
  const t = useTranslations('ring');
  const CartClient = useCartStore(
    useShallow((s) => ({ store: s.store, currency: s.currency })),
  );
  const formatPriceWithComma = useFormatPrice();

  const { store, currency } = CartClient;
  const CurrencyCode = CurrencySymbol[currency ?? Currency.usd];

  const benefits = [
    {
      icon: <UhxInfoDamage />,
      title: t('ring.infoModals.uhx.home.damage.title'),
      description: t('ring.infoModals.uhx.home.damage.p1'),
    },
    {
      icon: <UhxInfoReplacement />,
      title: t('ring.infoModals.uhx.text.c.title'),
      description: t('ring.infoModals.uhx.text.c.p1'),
    },
    {
      icon: <UhxInfoShield />,
      title: t('ring.infoModals.uhx.home.environmental.title'),
      description: t('ring.infoModals.uhx.home.environmental.p1'),
      disclaimer: t('ring.infoModals.uhx.text.d.disclaimer'),
    },
    {
      icon: <UhxBetaIcon />,
      title: t('ring.infoModals.uhx.home.beta.title'),
      description: t('ring.infoModals.uhx.home.beta.p1'),
      disclaimer: t('ring.infoModals.uhx.text.e.disclaimer'),
    },
    {
      icon: <UhxInfoSupport />,
      title: t('ring.infoModals.uhx.text.f.title'),
      description: t('ring.infoModals.uhx.text.f.p1'),
    },
  ];

  return (
    <InfoModalContainer>
      <InfoModalTitle>
        <h3>{t('ring.infoModals.uhx.title')}</h3>
        <p>
          <>
            {CurrencyCode}
            {formatPriceWithComma(
              UhxShopifyMap[store as ShopifyStore].Year1.price,
            )}
          </>
          {t('ring.infoModals.uhx.subtitle1')}
        </p>
        <ImageContainer className="uhx-image-container">
          <CustomImage
            src="web_v2/cart-info-modals/uhx-2.png"
            width={1376}
            height={808}
            alt={t('ring.UltrahumanX')}
          />
        </ImageContainer>
      </InfoModalTitle>
      <InfoModalDescription>
        <p>{t('ring.infoModals.uhx.home.description')}</p>
      </InfoModalDescription>
      <div className={styles.uhxInfoDescription}>
        {benefits.map((benefit, index) => (
          <div key={index} className={styles.benefit}>
            {benefit.icon}
            <h3>{benefit.title}</h3>
            <p className={styles.description}>{benefit.description}</p>
            <p className={styles.disclaimer}>{benefit.disclaimer}</p>
          </div>
        ))}
      </div>
    </InfoModalContainer>
  );
};

export const UhxPricingModal = () => (
  <PricingModal>
    <UhxCartInfoModal />
  </PricingModal>
);

export const HomeUhxPricingModal = () => (
  <PricingModal>
    <HomeUhxCartInfoModal />
  </PricingModal>
);
